import { useContext, useEffect, useState } from 'react';
import styles from './CalculationDetailModal.module.css';
import Modal from 'react-modal';
import CalculationExpressionContainer from './CalculationExpressionContainer';
import Alert from '../Alert/Alert';
import { AlertType } from '../Alert/AlertType';
import { FaRegRectangleXmark, FaHashtag } from 'react-icons/fa6';
import { RowDataSource } from '../model/RowDataSource';
import AppContext from '../../AppContext';

interface CalculationDetailModalProps {
  label: string;
  year: number;
  rowKey: string;
  cocode: number;
  rowType?: string;
  rowDataSource?: RowDataSource;
  sectionKey?: string;
  subSectionKey?: string;
  forceRollingPrior?: boolean;
}

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: '25%',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    maxHeight: '75%'
  }
};

export default function CalculationDetailModal({
  label,
  year,
  rowKey,
  cocode,
  rowType,
  rowDataSource,
  sectionKey,
  subSectionKey,
  forceRollingPrior
}: CalculationDetailModalProps) {
  const context = useContext(AppContext);
  const periodType = context?.periodType;
  const quarterlyViewMode = context?.quarterlyViewMode;
  const [modalIsOpen, setModalIsOpen] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [currentYearExpression, setCurrentYearExpression] = useState<string>();
  const [priorYearExpression, setPriorYearExpression] = useState<string>();

  // Compute prior year and periodType for the second expression container
  function getPriorYearAndPeriodType(): { priorYear: number; priorPeriodType: string | null } {
    const isQuarterly = periodType?.startsWith('QUARTER_');
    if (!isQuarterly) {
      return { priorYear: year - 1, priorPeriodType: null };
    }

    const isComparable = !forceRollingPrior && quarterlyViewMode === 'QTR_COMPARABLE';
    if (isComparable) {
      // Comparable: same quarter, prior year
      return { priorYear: year - 1, priorPeriodType: periodType };
    }

    // Rolling: previous quarter
    const quarterNum = parseInt(periodType?.replace('QUARTER_', '') || '1');
    if (quarterNum === 1) {
      return { priorYear: year - 1, priorPeriodType: 'QUARTER_4' };
    }
    return { priorYear: year, priorPeriodType: `QUARTER_${quarterNum - 1}` };
  }

  const { priorYear, priorPeriodType } = getPriorYearAndPeriodType();

  useEffect(() => {
    if (
      currentYearExpression &&
      priorYearExpression &&
      currentYearExpression !== priorYearExpression
    ) {
      setShowAlert(true);
    } else {
      setShowAlert(false);
    }
  }, [currentYearExpression, priorYearExpression]);

  function openCalcDetails() {
    setModalIsOpen(true);
  }

  return (
    <>
      <FaHashtag
        onClick={openCalcDetails}
        aria-label="Open calculation detail modal"
        className={styles.open_modal_button}
        id={`open_calculation_detail_modal_${rowDataSource}_${rowKey}`}
        title="Calculation Details"
      />

      <Modal
        id="calc_details_modal"
        isOpen={modalIsOpen}
        style={modalStyles}
        onRequestClose={() => setModalIsOpen(false)}
        appElement={document.getElementById('root') as HTMLElement}
        contentLabel="Calculation Details">
        <div className={styles.title}>
          <h1 id="context_modal_title">{`Calculation Details for ${label}`}</h1>
          <FaRegRectangleXmark
            size={'2rem'}
            onClick={() => setModalIsOpen(false)}
            aria-label="Close"
            id="close_modal"
          />
        </div>
        {showAlert && (
          <Alert
            alertType={AlertType.WARNING}
            content={
              'Please note: this calculation formula changed from last year to this year.'
            }></Alert>
        )}
        <CalculationExpressionContainer
          year={year}
          rowKey={rowKey}
          cocode={cocode}
          rowDataSource={rowDataSource}
          rowType={rowType}
          updateExpression={setCurrentYearExpression}
          sectionKey={sectionKey}
          subSectionKey={subSectionKey}
        />
        <CalculationExpressionContainer
          year={priorYear}
          rowKey={rowKey}
          cocode={cocode}
          rowDataSource={rowDataSource}
          rowType={rowType}
          updateExpression={setPriorYearExpression}
          sectionKey={sectionKey}
          subSectionKey={subSectionKey}
          overridePeriodType={priorPeriodType}
        />
      </Modal>
    </>
  );
}
